import { useCallback, useRef, useEffect, useState } from "react";
import { cn } from "~/widgets/lib/utils";
import { pushToGoogleAds } from "~/widgets/lib/tracking";
import { fetchCourseSummary } from "~/widgets/lib/api";
import type { BookNowButtonProps } from "./types";

// Button style variants
const buttonStyleClasses = {
  rounded: "rounded-lg",
  square: "rounded-none",
  pill: "rounded-full",
} as const;

export function BookNowButton({
  listingId,
  businessId,
  apiUrl,
  buttonText = "Book Now",
  soldOutText = "Sold Out",
  buttonClassName,
  buttonStyle = "pill",
  theme = "light",
  widgetBaseUrl,
  gtagConversionId,
  gtagConversionLabel,
}: BookNowButtonProps) {
  const overlayRef = useRef<HTMLElement | null>(null);

  // Reflect sold-out / fully-held state in the label: a full course (or one whose
  // seats are all held by live queue offers) shows `soldOutText` (default "Sold
  // Out"; a waitlist listing sets "Join Waiting List") instead of "Book Now".
  // Clicking still opens the overlay, which routes a sold-out course to the
  // waiting list when the listing has one. Falls back to the bookable label on
  // fetch error — never block booking on a summary lookup. Server-cached (~5 min).
  const [isSoldOut, setIsSoldOut] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchCourseSummary(listingId, apiUrl)
      .then((summary) => {
        if (!cancelled) setIsSoldOut(!!summary.isSoldOut);
      })
      .catch(() => {
        // Leave as bookable — a failed lookup must not hide the Book Now button.
      });
    return () => {
      cancelled = true;
    };
  }, [listingId, apiUrl]);

  const handleClose = useCallback(() => {
    if (overlayRef.current && overlayRef.current.parentNode) {
      overlayRef.current.parentNode.removeChild(overlayRef.current);
      overlayRef.current = null;
    }
  }, []);

  const handleComplete = useCallback(() => {
    const event = new CustomEvent("baluu:booking-completed", {
      bubbles: true,
      composed: true,
      detail: {
        listingId,
        businessId,
      },
    });
    document.dispatchEvent(event);

    setTimeout(() => {
      handleClose();
    }, 3000);
  }, [listingId, businessId, handleClose]);

  const handleOpen = useCallback(() => {
    const overlay = document.createElement("baluu-checkout-overlay");

    overlay.setAttribute("listing-id", listingId);
    overlay.setAttribute("business-id", businessId);
    if (apiUrl) overlay.setAttribute("api-url", apiUrl);
    if (theme) overlay.setAttribute("theme", theme);
    if (widgetBaseUrl) overlay.setAttribute("widget-base-url", widgetBaseUrl);

    overlayRef.current = overlay;
    document.body.appendChild(overlay);

    const handleOverlayClose = () => {
      handleClose();
    };

    const handleOverlayComplete = (e: Event) => {
      handleComplete();

      if (gtagConversionId && gtagConversionLabel) {
        const detail = (e as CustomEvent).detail || {};
        pushToGoogleAds(gtagConversionId, gtagConversionLabel, {
          listingId,
          businessId,
          purchaseId: detail.purchaseId,
          total: detail.total,
          currency: detail.currency,
        });
      }
    };

    overlay.addEventListener("baluu:checkout-close", handleOverlayClose);
    overlay.addEventListener("baluu:booking-completed", handleOverlayComplete);
  }, [
    listingId,
    businessId,
    apiUrl,
    theme,
    widgetBaseUrl,
    gtagConversionId,
    gtagConversionLabel,
    handleClose,
    handleComplete,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current);
      }
    };
  }, []);

  // Get border radius class based on button style
  const borderRadiusClass =
    buttonStyleClasses[buttonStyle] || buttonStyleClasses.pill;

  return (
    <div className="baluu-widget-root">
      <button
        onClick={handleOpen}
        className={cn(
          // Stable hook class for dashboard "Custom CSS" overrides - do not rename
          "baluu-book-button",
          "inline-flex items-center justify-center",
          "px-6 py-3 font-medium text-base",
          "bg-primary text-primary-foreground",
          "transition-all cursor-pointer",
          "hover:opacity-90 hover:shadow-lg",
          "active:scale-95",
          borderRadiusClass,
          buttonClassName,
        )}
      >
        {isSoldOut ? soldOutText : buttonText}
      </button>
    </div>
  );
}

